import { AxiosResponse } from 'axios';
import { BASE_URL } from 'src/config/api';
import client from '../client';
import { LiveListResponse } from 'src/admin-entity/interface/live/live-list.interface';
import {
    GetAvailableDisplaysResponse,
    GetDisplaysResponse,
    LiveDetailResponse,
    UpdateDisplaysResponse,
} from 'src/admin-entity/interface/live/live-detail.interface';
import { StartBroadcastResponse } from 'src/admin-entity/interface/live/live-start-broadcast-response';
import { LiveVodItem } from 'src/admin-entity/interface/live/live-vod.interface';
import { PinChatRequestT } from 'src/admin-entity/interface/live/pin-chat-request';
import { VoteDetailResponse, VoteListResponse } from 'src/admin-entity/interface/live/live-vote.interface';
import { ReserveGlobalSettingsResponse } from 'src/admin-entity/interface/live/live-global-setting-reserve.interface';
import { ReserveCartCancel } from 'src/admin-entity/interface/live/live-reserve-cart-cancel';
import { ReserveCartOptionsResponse } from 'src/admin-entity/interface/live/live-reserve-cart-options.interface';
import {
    LiveCoverSettingResponse,
    LsCoverImage,
    UpdateLiveCoverSettingRequest,
} from 'src/admin-entity/interface/live/live-cover-image.interface';
import { ReviewRoomGlobalSettings } from 'src/admin-entity/interface/live/live-global-setting-review-room.interface';
import {
    ReviewRoomSettings,
    ReviewRoomSettingsResponse,
} from 'src/admin-entity/interface/live/live-review-room-settings.interface';
import {
    SimulcastTarget,
    SimulcastTargetStatus,
} from 'src/admin-entity/interface/live/live-simulcast-target.interface';
import { LiveTimerEndAtResponse } from 'src/admin-entity/interface/live/live-timer-end-at.interface';
import { LiveStreamLogResponse } from 'src/admin-entity/interface/live/live-stream-log.interface';

export const getLiveList = (page: number, take: number): Promise<AxiosResponse<LiveListResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls?page=${page}&take=${take}`);
};

export const getLiveDetail = (id: string): Promise<AxiosResponse<LiveDetailResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${id}`);
};

export const updateLiveName = (id: string, title: string): Promise<AxiosResponse<StartBroadcastResponse>> => {
    return client.put(`${BASE_URL}/v1/admin/ls/${id}`, { title: title });
};

export const startBroadcast = (id: string): Promise<AxiosResponse<StartBroadcastResponse>> => {
    return client.put(`${BASE_URL}/v1/admin/ls/${id}`, { status: 'live' });
};

export const closeBroadcast = (id: string): Promise<AxiosResponse<StartBroadcastResponse>> => {
    return client.put(`${BASE_URL}/v1/admin/ls/${id}`, { status: 'ended' });
};

export const createLiveRoom = (): Promise<AxiosResponse<LiveDetailResponse>> => {
    return client.post(`${BASE_URL}/v1/admin/ls`);
};

export const getisLiveDetail = (): Promise<AxiosResponse<LiveDetailResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/live`);
};

export const getDisplays = (): Promise<AxiosResponse<GetDisplaysResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/settings`);
};

export const getAvailableDisplays = (): Promise<AxiosResponse<GetAvailableDisplaysResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/settings/available-displays`);
};

export const updateDisplays = (
    liveEndSettings: boolean,
    id: number,
): Promise<AxiosResponse<UpdateDisplaysResponse>> => {
    return client.put(`${BASE_URL}/v1/admin/ls/settings`, { liveEndSettings, liveEndDisplayCategoryId: id });
};

export const limitLiveViewer = (lsBroadcastId: number, viewerLimit: number): Promise<AxiosResponse> => {
    return client.put(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/viewer/limit`, { viewerLimit });
};

export const getLiveVodList = (lsBroadcastId: number): Promise<AxiosResponse<LiveVodItem[]>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/recording`);
};

export const getLiveVodDetail = (
    lsBroadcastId: number,
    lsBroadcastRecordingId: number,
): Promise<AxiosResponse<LiveVodItem>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/recording/${lsBroadcastRecordingId}`);
};

export const liveVodDownload = (lsBroadcastId: number, lsBroadcastRecordingId: number): Promise<AxiosResponse> => {
    return client.post(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/recording/${lsBroadcastRecordingId}/download/mp4`);
};

export const pinLiveChat = (lsBroadcastId: number, body: PinChatRequestT): Promise<AxiosResponse> => {
    return client.post(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/pin-chat`, body);
};

export const unpinLiveChat = (lsBroadcastId: number): Promise<AxiosResponse> => {
    return client.delete(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/pin-chat`);
};

export const voteList = (lsBroadcastId: number): Promise<AxiosResponse<VoteListResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/vote`);
};

export const voteDetail = (lsBroadcastId: number, voteId: number): Promise<AxiosResponse<VoteDetailResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/vote/${voteId}`);
};

export const liveReserveSetting = (): Promise<AxiosResponse<ReserveGlobalSettingsResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/global-settings/reserve`);
};

export const updateLiveReserveSetting = (
    isReserveCancelEnabled: boolean,
    maxReserveCancelCount: number,
    isUnpaidPenaltyEnabled: boolean,
    maxUnpaidPenaltyCount: number,
): Promise<AxiosResponse<ReserveGlobalSettingsResponse>> => {
    return client.put(`${BASE_URL}/v1/admin/ls/global-settings/reserve`, {
        isReserveCancelEnabled,
        maxReserveCancelCount,
        isUnpaidPenaltyEnabled,
        maxUnpaidPenaltyCount,
    });
};

export const reserveCartCancel = (lsBroadcastId: number): Promise<AxiosResponse<ReserveCartCancel>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/reserve-cart-cancel`);
};

export const updateReserveCartCancel = (
    lsBroadcastId: number,
    data: ReserveCartCancel,
): Promise<AxiosResponse<ReserveCartCancel>> => {
    return client.put(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/reserve-cart-cancel`, data);
};

export const reserveCartOptions = (
    lsBroadcastId: number,
    productId: number,
): Promise<AxiosResponse<ReserveCartOptionsResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/reserve-cart/ls-broadcast/${lsBroadcastId}/product/${productId}/options`);
};

export const DeleteReserveCartOptions = (
    lsBroadcastId: number,
    productId: number,
    combinationId: number,
): Promise<AxiosResponse> => {
    return client.delete(
        `${BASE_URL}/v1/admin/reserve-cart/ls-broadcast/${lsBroadcastId}/product/${productId}/options/${combinationId}`,
    );
};

export const groupChatGlobalManager = (): Promise<AxiosResponse<{ isGroupedPurchaseChantMessage: boolean }>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/global-settings/chat-message`);
};

export const updateGroupChatGlobalManager = (data: {
    isGroupedPurchaseChantMessage: boolean; // 구매 완료 메시지 묶음 여부 [true=묶음, false=개별]
}): Promise<AxiosResponse> => {
    return client.put(`${BASE_URL}/v1/admin/ls/global-settings/chat-message`, data);
};

export const groupChatManager = (
    lsBroadcastId: number,
): Promise<AxiosResponse<{ isGroupedPurchaseChantMessage: boolean }>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/chat-message`);
};

export const updateGroupChatManager = (
    lsBroadcastId: number,
    data: {
        isGroupedPurchaseChantMessage: boolean; // 구매 완료 메시지 묶음 여부 [true=묶음, false=개별]
    },
): Promise<AxiosResponse> => {
    return client.put(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/chat-message`, data);
};

export const getSimulcastTargetList = (lsBroadcastId: number): Promise<AxiosResponse<SimulcastTarget[]>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/simulcast/target`);
};

export const updateSimulcastTarget = (
    lsBroadcastId: number,
    lsBroadcastSimulcastTargetId: number,
    data: Record<string, unknown>,
): Promise<AxiosResponse> => {
    return client.put(
        `${BASE_URL}/v1/admin/ls/${lsBroadcastId}/simulcast/target/${lsBroadcastSimulcastTargetId}`,
        data,
    );
};

export const startSimulcastTarget = (
    lsBroadcastId: number,
    lsBroadcastSimulcastTargetId: number,
): Promise<AxiosResponse> => {
    return client.post(
        `${BASE_URL}/v1/admin/ls/${lsBroadcastId}/simulcast/target/${lsBroadcastSimulcastTargetId}/start`,
    );
};

export const getSimulcastTargetStatus = (
    lsBroadcastId: number,
    lsBroadcastSimulcastTargetId: number,
): Promise<AxiosResponse<SimulcastTargetStatus>> => {
    return client.get(
        `${BASE_URL}/v1/admin/ls/${lsBroadcastId}/simulcast/target/${lsBroadcastSimulcastTargetId}/status`,
    );
};

export const stopSimulcastTarget = (
    lsBroadcastId: number,
    lsBroadcastSimulcastTargetId: number,
): Promise<AxiosResponse> => {
    return client.post(
        `${BASE_URL}/v1/admin/ls/${lsBroadcastId}/simulcast/target/${lsBroadcastSimulcastTargetId}/stop`,
    );
};

export const getLiveTimerEndAt = (lsBroadcastId: number): Promise<AxiosResponse<LiveTimerEndAtResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/timer-end-at`);
};

export const updateLiveTimerEndAt = (
    lsBroadcastId: number,
    data: { timerEndAt: string; timerMessage: string },
): Promise<AxiosResponse<LiveTimerEndAtResponse>> => {
    return client.put(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/timer-end-at`, data);
};

export const createLsCoverImage = (
    data: FormData,
    setProgress: (value: any) => void,
): Promise<AxiosResponse<LsCoverImage>> => {
    return client.post(`${BASE_URL}/v1/admin/ls-cover-image`, data, {
        onUploadProgress: (e: ProgressEvent) => {
            setProgress(Math.floor((e.loaded / e.total) * 100));
        },
    });
};

export const updateLsCoverImageName = (lsCoverImage: number, name: string): Promise<AxiosResponse<LsCoverImage>> => {
    return client.put(`${BASE_URL}/v1/admin/ls-cover-image/${lsCoverImage}/name`, { name });
};

export const deleteLsCoverImage = (lsCoverImage: number): Promise<AxiosResponse<any>> => {
    return client.delete(`${BASE_URL}/v1/admin/ls-cover-image/${lsCoverImage}`);
};

export const getLiveCoverSetting = (lsBroadcastId: number): Promise<AxiosResponse<LiveCoverSettingResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/cover`);
};

export const updateLiveCoverSetting = (
    lsBroadcastId: number,
    data: UpdateLiveCoverSettingRequest,
): Promise<AxiosResponse<any>> => {
    return client.put(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/cover`, data);
};

export const deleteLiveCoverSetting = (lsBroadcastId: number): Promise<AxiosResponse<any>> => {
    return client.delete(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/cover`);
};

export const getReviewRoomGlobalSettings = async (): Promise<ReviewRoomGlobalSettings> => {
    const response = await client.get(`${BASE_URL}/v1/admin/ls/global-settings/review-room`);
    return response.data;
};

export const updateReviewRoomGlobalSettings = async (
    data: ReviewRoomGlobalSettings,
): Promise<ReviewRoomGlobalSettings> => {
    const response = await client.put(`${BASE_URL}/v1/admin/ls/global-settings/review-room`, data);
    return response.data;
};

export const getReviewRoomSettings = async (lsBroadcastId: number): Promise<ReviewRoomSettingsResponse> => {
    const response = await client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/review-room`);
    return response.data;
};

export const updateReviewRoomSettings = async (
    lsBroadcastId: number,
    data: ReviewRoomSettings,
): Promise<ReviewRoomSettingsResponse> => {
    const response = await client.put(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/settings/review-room`, data);
    return response.data;
};

export const getLiveStreamLog = (lsBroadcastId: number): Promise<AxiosResponse<LiveStreamLogResponse>> => {
    return client.get(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/stream-log`);
};
